import React from 'react';
import styled from 'styled-components';

// WhatsApp only lets us message a worker free of charge within 24h of THEIR last
// inbound message. This indicator shows, per conversation, how long is left in
// that window so recruiters can work the soonest-to-expire chats first.
const WINDOW_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

export type SlaTone = 'green' | 'amber' | 'red' | 'expired';

export interface SlaInfo {
    /**
     * The 24h free-messaging window has lapsed (or the worker has never messaged
     * us). "Expired", not "closed" — the conversation is still open; we just can't
     * send a free-form message until the worker replies (templates aside).
     */
    expired: boolean;
    /** Milliseconds until the 24h lockout. -Infinity when there's no inbound at all. */
    msRemaining: number;
    /** Compact glanceable label, e.g. "5h left", "45m left", "Expired". */
    label: string;
    tone: SlaTone;
}

/**
 * Compute the 24h-window status from the last inbound timestamp.
 * `now` is injectable so the sidebar can compute a whole list against one clock
 * (and so it's testable). Recomputed on each render — list/socket updates refresh
 * the relative time without needing a per-second ticker.
 */
export const getSlaInfo = (lastInboundAt?: string | null, now: number = Date.now()): SlaInfo => {
    if (!lastInboundAt) {
        return { expired: true, msRemaining: -Infinity, label: 'Expired', tone: 'expired' };
    }
    const last = new Date(lastInboundAt).getTime();
    if (Number.isNaN(last)) {
        return { expired: true, msRemaining: -Infinity, label: 'Expired', tone: 'expired' };
    }

    const msRemaining = WINDOW_MS - (now - last);
    if (msRemaining <= 0) {
        return { expired: true, msRemaining, label: 'Expired', tone: 'expired' };
    }

    let label: string;
    if (msRemaining >= HOUR_MS) {
        label = `${Math.floor(msRemaining / HOUR_MS)}h left`;
    } else {
        // Show at least "1m left" so a sub-minute window never reads as "0m".
        label = `${Math.max(1, Math.floor(msRemaining / MINUTE_MS))}m left`;
    }

    const tone: SlaTone = msRemaining > 6 * HOUR_MS ? 'green' : msRemaining >= HOUR_MS ? 'amber' : 'red';
    return { expired: false, msRemaining, label, tone };
};

const TONE_COLORS: Record<SlaTone, { bg: string; bgDark: string; fg: string; fgDark: string }> = {
    green: { bg: '#DCFCE7', bgDark: '#14532D', fg: '#166534', fgDark: '#86EFAC' },
    amber: { bg: '#FEF3C7', bgDark: '#78350F', fg: '#92400E', fgDark: '#FCD34D' },
    red: { bg: '#FEE2E2', bgDark: '#7F1D1D', fg: '#991B1B', fgDark: '#FCA5A5' },
    expired: { bg: '#F1F5F9', bgDark: '#334155', fg: '#64748B', fgDark: '#94A3B8' },
};

const Chip = styled.span<{ $tone: SlaTone; $isDarkMode: boolean }>`
    display: inline-block;
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.5;
    white-space: nowrap;
    background-color: ${props => (props.$isDarkMode ? TONE_COLORS[props.$tone].bgDark : TONE_COLORS[props.$tone].bg)};
    color: ${props => (props.$isDarkMode ? TONE_COLORS[props.$tone].fgDark : TONE_COLORS[props.$tone].fg)};
`;

interface SessionSlaIndicatorProps {
    lastInboundAt?: string | null;
    /** Shared clock so a whole list renders against one `now`. */
    now?: number;
    isDarkMode?: boolean;
}

/** Compact 24h-window chip: "5h left" (green > 6h, amber 1–6h, red < 1h) / "Expired" (grey). */
const SessionSlaIndicator: React.FC<SessionSlaIndicatorProps> = ({ lastInboundAt, now, isDarkMode = false }) => {
    const sla = getSlaInfo(lastInboundAt, now);
    const title = sla.expired
        ? '24h WhatsApp window expired — the worker must reply before we can message again (templates not supported yet)'
        : `${sla.label} in the 24h free-messaging window`;
    return (
        <Chip $tone={sla.tone} $isDarkMode={isDarkMode} title={title}>
            {sla.label}
        </Chip>
    );
};

export default SessionSlaIndicator;
